<template>
    <!-- Modal -->
    <p-modal
        name="add-buyer-modal"
        class="add-buyer-modal-container"
        :classes="'add-buyer-vm-modal'"
        :width="isUserModal ? 870 : 1108"
        @before-open="resetBuyerInfoError"
        @closed="closeModal"
    >
        <div class="add-buyer-modal-content" :class="{ 'contact-modal': isRedesigned, 'user-modal': isUserModal }">
            <header v-if="isRedesigned" class="contact-modal__header">
                <div>
                    <p class="contact-modal__eyebrow">{{ isContactInfo ? 'Your Contact Info' : 'Account Users' }}</p>
                    <h2 class="contact-modal__title">{{ modalTitle }}</h2>
                </div>
                <div class="contact-modal__header-side">
                    <p class="contact-modal__required"><span aria-hidden="true">*</span> Required</p>
                    <button type="button" class="contact-modal__close" aria-label="Close" @click="() => { shouldEmitOnClose = true; closeModal(buyerData) }">
                        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">
                            <path d="M3 3l10 10M13 3L3 13" />
                        </svg>
                    </button>
                </div>
            </header>
            <div v-else class="modal-header">
                <fa
                    icon="user-friends"
                    fixed-width
                />
                <h6 v-if="modelAction === 'edit'" >User Info</h6>
                <h6 v-else-if="modelAction === 'add'" > New User</h6>
                <div class="modal-required"> Required
                    <div class="required-dot modal-required-dot"></div>
                </div>
            </div>
            <div class="modal-body">
                <!-- User Permission Status for Admin/Rep -->
                <div v-if="modelName === 'adminProfile'" class="row mb-3">
                    <div class="col-md-12">
                        <strong class="user-permission-status">
                            User PERMISSION Status:
                            <span class="text-uppercase user-role">
                                {{authUser.role_label}}
                            </span>
                        </strong>
                    </div>
                </div>

                <!-- Profile Picture Section for Admin/Rep -->
                <div v-if="modelName === 'adminProfile'" class="row mb-4">
                    <div class="col-md-12">
                        <div class="profile-section">
                            <div class="profile-picture-wrapper d-flex align-items-start">
                                <div class="profile-image-container">
                                    <img
                                        v-if="profilePicturePreview || authUser.profile_picture_url"
                                        class="rounded-circle profile-image"
                                        :src="profilePicturePreview || authUser.profile_picture_url"
                                        width="120"
                                        height="120"
                                    >
                                    <div v-else class="profile-placeholder rounded-circle">
                                        <fa
                                            icon="user"
                                            fixed-width
                                            class="profile-icon"
                                        />
                                    </div>
                                </div>
                                <input
                                    id="profile_picture_file"
                                    ref="profilePictureFileRef"
                                    type="file"
                                    accept="image/*"
                                    class="d-none"
                                    @change="onProfilePictureChange"
                                >
                                <a
                                    class="edit-icon profile-edit-icon ms-3"
                                    @click="$refs.profilePictureFileRef.click()"
                                    :class="{ 'disabled': isUploadingPicture }"
                                >
                                    <fa icon="edit" fixed-width/>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <p v-if="isCompanyAdmin && isRedesigned" class="contact-modal__notice">
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
                        <circle cx="8" cy="8" r="6.5" />
                        <path d="M8 7v4.5M8 4.5v.5" stroke-linecap="round" />
                    </svg>
                    {{ isContactInfo ? 'Contact changes' : 'User changes' }} are reviewed by Pacific Silk and go live within one business day.
                </p>
                <div v-else-if="isCompanyAdmin && modelName !== 'adminProfile'" class="alert-message">
                    <i class="fas fa-exclamation-triangle"></i>
                    <strong v-if="modelAction === 'edit' "> USER INFO </strong>
                    <strong v-else-if="modelAction === 'add' "> NEW USER </strong>
                    {{ $t('update_alert_msg') }}
                </div>
                <form @submit.prevent="submitAddBuyerModal">
                    <div class="contact-modal__scroll">
                    <div class="row mb-5 mt-3">
                        <div class="col-md-12" :class="{ 'user-modal__permission-col': isUserModal }">
                            <div v-if="isContactInfo" class="contact-modal__permission">
                                <img
                                    v-if="authUser.profile_picture_url"
                                    :src="authUser.profile_picture_url"
                                    alt=""
                                    class="contact-modal__avatar"
                                >
                                <span v-else class="contact-modal__avatar" aria-hidden="true">{{ authInitials }}</span>
                                <div>
                                    <p class="contact-modal__permission-label">
                                        Permission level
                                        <popper
                                            :visible-arrow="false"
                                            ref="contactInfoPopperRef"
                                            :options="{
                                              placement: 'right-top',
                                              modifiers: { offset: { offset: '0px,10px' } },
                                            }"
                                            trigger="hover"
                                        >
                                            <div class="popper contact-info-popper">
                                              <div class="popover-container">
                                                  <p class="text-start">
                                                        Contact Pacific Silk (949)496-8437 for all permission status changes
                                                  </p>
                                              </div>
                                            </div>
                                            <span slot="reference" class="contact-modal__info" tabindex="0" aria-label="About permission changes">
                                                <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
                                                    <circle cx="8" cy="8" r="6.5" />
                                                    <path d="M8 7v4.5M8 4.5v.5" stroke-linecap="round" />
                                                </svg>
                                            </span>
                                        </popper>
                                    </p>
                                    <span class="contact-modal__role">{{ authUser.role_label }}</span>
                                </div>
                                <p class="contact-modal__permission-text">
                                    Your permission level can be changed by another admin on the account or by Pacific Silk.
                                </p>
                            </div>
                            <fieldset v-if="isUserModal" class="user-modal__permission">
                                <legend class="user-modal__legend">Permission level <span class="user-modal__required-mark" aria-hidden="true">*</span></legend>
                                <div class="user-modal__roles">
                                    <label
                                        v-for="option in roleOptions"
                                        :key="option.value"
                                        class="user-modal__role"
                                        :class="{ 'is-selected': formData.role === option.value }"
                                    >
                                        <input
                                            v-model="formData.role"
                                            type="radio"
                                            name="user-role"
                                            class="user-modal__role-input"
                                            :value="option.value"
                                        >
                                        <span class="user-modal__radio" aria-hidden="true" />
                                        <span>
                                            <span class="user-modal__role-name">{{ option.value }}</span>
                                            <span class="user-modal__role-desc">{{ option.description }}</span>
                                        </span>
                                    </label>
                                </div>
                                <small class="text-danger">{{ errors.role }}</small>
                            </fieldset>
                        </div>
                    </div>
                    <div class="row mb-3 align-items-start">
                        <div class="col-md-6">
                            <text-field
                                id="first-name"
                                v-model="formData.first_name"
                                mandatory
                                :label="isRedesigned ? 'First name' : 'First Name'"
                                label-class="fields-font"
                            />
                            <small class="text-danger">{{ errors.first_name }}</small>
                        </div>
                        <div class="col-md-6">
                            <text-field
                                id="last-name"
                                v-model="formData.last_name"
                                mandatory
                                :label="isRedesigned ? 'Last name' : 'Last Name'"
                                label-class="fields-font"
                            />
                            <small class="text-danger">{{ errors.last_name }}</small>
                        </div>
                    </div>
                    <div class="row mb-3 align-items-start">
                        <div class="col-md-4">
                            <text-field
                                id="buyer-phone"
                                v-model="formData.phone_number"
                                v-mask="'(###) ###-####'"
                                mandatory
                                label="Phone"

                            />
                            <small class="text-danger">{{ errors.phone_number }}</small>
                        </div>
                        <div class="col-md-4">
                            <text-field
                                id="buyer-cell-phone"
                                v-model="formData.cell_phone_number"
                                v-mask="'(###) ###-####'"
                                mandatory
                                :label="isRedesigned ? 'Cell phone' : 'Cell Phone'"
                            />
                            <small class="text-danger">{{ errors.cell_phone_number }}</small>
                        </div>
                        <div class="col-md-4">
                            <text-field
                                id="email"
                                v-model="formData.email"
                                mandatory
                                label="Email"
                                label-class="fields-font"
                            />
                            <small class="text-danger">{{ errors.email }}</small>
                        </div>
                    </div>
                    <div v-if="isUserModal && modelAction === 'edit'" class="user-modal__password">
                        <div v-if="!isPasswordEdit" class="user-modal__password-row">
                            <div>
                                <h3 class="user-modal__password-title">Password</h3>
                                <p class="user-modal__password-text">Email {{ formData.first_name || 'this user' }} a secure link to set a new password.</p>
                            </div>
                            <button type="button" class="user-modal__outline-btn" @click="setIsPasswordEdit">Send reset link</button>
                        </div>
                        <div v-else class="user-modal__password-fields">
                            <div>
                                <text-field
                                    id="new-password"
                                    v-model="formData.new_password"
                                    type="password"
                                    label="New password"
                                    label-class="fields-font"
                                    autocomplete="new-password-unique"
                                />
                                <small class="text-danger">{{ errors.new_password }}</small>
                            </div>
                            <div>
                                <text-field
                                    id="confirm-password"
                                    v-model="formData.confirm_password"
                                    type="password"
                                    label="Re-enter password"
                                    label-class="fields-font"
                                    autocomplete="new-password-unique"
                                />
                                <small class="text-danger">{{ errors.confirm_password }}</small>
                            </div>
                            <button type="button" class="user-modal__outline-btn" @click="setIsPasswordEdit">Save password</button>
                        </div>
                    </div>
                    <div v-if="modelName === 'adminProfile'" class="row align-items-start password-field-margin" >
                        <div class="d-flex align-items-center mb-3">
                            <h6 class="buyer-password" v-if="!isPasswordEdit"> Password </h6>
                            <p-button
                                v-if="!isPasswordEdit"
                                variant="light"
                                class="py-2 px-2 reset-icon"
                                @click="setIsPasswordEdit"
                            >
                                <fa
                                    v-if="!isPasswordEdit"
                                    icon="retweet"
                                    fixed-width
                                />
                                <span class="ms-1 reset-text">{{ editPasswordText }}</span>
                            </p-button>
                            <div
                                v-if="isPasswordEdit"
                                class="reset-password d-flex mt-2"
                            >
                                <div class="me-2">
                                    <text-field
                                        id="new-password"
                                        v-model="formData.new_password"
                                        type="password"
                                        label="New Password"
                                        label-class="fields-font"
                                        autocomplete="new-password-unique"
                                        :input-class="newPasswordClass"
                                    />
                                    <small class="text-danger">{{ errors.new_password }}</small>
                                </div>
                                <div>
                                    <text-field
                                        id="confirm-password"
                                        v-model="formData.confirm_password"
                                        type="password"
                                        label="Re-enter Password"
                                        label-class="fields-font"
                                        autocomplete="new-password-unique"
                                        :input-class="confirmPasswordClass"
                                    />
                                    <small class="text-danger">{{ errors.confirm_password }}</small>
                                </div>
                                    <p-button
                                        v-if="isPasswordEdit"
                                        @click="setIsPasswordEdit"
                                        class="save-button"
                                        type="button"
                                    >
                                        {{ editPasswordText }}
                                    </p-button>
                            </div>
                        </div>
                    </div>
                    <div v-if="modelName === 'accountUsers' && modelAction  === 'add'" class="row align-items-start password-field-margin" >
                        <div class="d-flex flex-column mb-3">
                            <div class="d-flex mb-2">
                                <h6 class="buyer-password"> Create Password </h6>
                            </div>
                            <div class="reset-password d-flex mt-2">
                                <div class="me-2">
                                    <text-field
                                        id="new-password"
                                        v-model="formData.new_password"
                                        type="password"
                                        label="Enter Password"
                                        label-class="fields-font"
                                        autocomplete="new-password"
                                        :input-class="newPasswordClass"
                                    />
                                    <small class="text-danger">{{ errors.new_password }}</small>
                                </div>
                                <div>
                                    <text-field
                                        id="confirm-password"
                                        v-model="formData.confirm_password"
                                        type="password"
                                        label="Re-enter Password"
                                        label-class="fields-font"
                                        autocomplete="new-password-unique"
                                        :input-class="confirmPasswordClass"
                                    />
                                    <small class="text-danger">{{ errors.confirm_password }}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div class="d-flex justify-content-between button-groups bottom-fields-margin" :class="{ 'user-modal__footer': isUserModal }">
                        <popper
                            v-if="isCompanyAdmin && buyerData && modelName === 'accountUsers' && modelName !== 'adminProfile'"
                            ref="deleteUserPopperRef"
                            trigger="clickToToggle"
                            :options="{
                                placement: 'top-start',
                                modifiers: { offset: { offset: '0px,10px' } },
                            }"
                        >
                            <div class="popper">
                                <div class="popover-container delete-user-popper-container">
                                    <p class="delete-user-popper-container-text">PERMANENTLY Delete this USER?</p>
                                    <div class="d-flex justify-content-center align-items-center mt-3">
                                        <a class="me-2 delete-user-yes" @click="deleteBuyer">
                                            YES
                                        </a>
                                        <a class="ms-2 delete-user-no" @click="$refs.deleteUserPopperRef.doClose()">
                                            NO
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div slot="reference">
                                <button type="button" class="delete-icon">
                                    <fa
                                        icon="trash-alt"
                                        fixed-width
                                    /> <span class="delete-user">Delete user</span>
                                </button>
                            </div>
                        </popper>

                        <div class="d-flex align-items-center ms-auto">
                            <p-button
                                :disabled="formLoading"
                                class="me-2 text-capitalize btn-per-item"
                            >
                                {{ modelName === 'adminProfile' ? (!formLoading ? 'Save Changes' : 'Saving...') : (!formLoading ? 'Request Update' : 'Saving...') }}
                            </p-button>
                            <p-button
                                type="button"
                                class="bg-secondary text-capitalize btn-per-item"
                                @click="() => { shouldEmitOnClose = true; closeModal(buyerData) }"
                            >
                                Cancel
                            </p-button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </p-modal>
</template>

<script>
import PModal from '~/components/core/PModal'
import TextField from '~/components/core/Inputs/TextField'
import TextareaField from '~/components/core/Inputs/TextareaField'
import PButton from '~/components/core/PButton'
import PSelect from '~/components/core/Inputs/PSelect'
import { REP_NAME_OPTIONS, ROLES } from '~/constants'
import { mapActions, mapGetters, mapState } from 'vuex'
import DraftOrdersDropdown from '~/components/DraftOrdersDropdown.vue'
import { mask } from 'vue-the-mask'

export default {
    name: 'AddBuyerModal',
    components: { DraftOrdersDropdown, PButton, TextareaField, TextField, PModal, PSelect },
    directives: { mask },
    props: {
        companyId: {
            type: String | Number,
            default: null
        },
        buyerData: {
            type: Object,
            default: null
        },
        orderBuyer: {
            type: String | Number,
            default: null
        },
        orderId: {
            type: String | Number,
            default: null
        },
        modelName: {
            type: String,
            default: ''
        },
        modelAction: {
            type: String,
            default: ''
        },
    },
    data: () => ({
        editMode: false,
        formLoading: false,
        formData: {
            is_default: false,
            first_name: '',
            last_name: '',
            phone_number: '',
            cell_phone_number: '',
            email: '',
            note: '',
            buyers_rep: '',
            role: null,
            new_password: '',
            confirm_password: ''
        },
        repItems: REP_NAME_OPTIONS,
        roleOptions: [
            { value: 'Admin', description: 'Full account access to all website features.' },
            { value: 'Buyer', description: 'Builds, submits and tracks orders for the account. Limited account editing capabilities.' },
            { value: 'Associate', description: 'Browse all products (no cost info). Create Collections and share Line Sheets. Cannot place orders.' }
        ],
        errors: {
            first_name: null,
            last_name: null,
            email: null,
            phone_number: null,
            cell_phone_number: null,
            note: null,
            new_password: null,
            confirm_password: null
        },
        isPasswordEdit: false,
        editPasswordText: 'Reset',
        shouldEmitOnClose: false,
        isUploadingPicture: false,
        profilePictureFile: null,
        profilePicturePreview: null,
    }),
    watch: {
        buyerData (val) {
            if (this.modelName === 'adminProfile') {
                // For admin profile, use authUser data
                this.editMode = true
                this.formData = {
                    first_name: this.authUser?.first_name || '',
                    last_name: this.authUser?.last_name || '',
                    email: this.authUser?.email || '',
                    phone_number: this.authUser?.phone_number || '',
                    cell_phone_number: this.authUser?.cell_phone_number || '',
                    new_password: '',
                    confirm_password: ''
                }
            } else if (val) {
                this.editMode = true
                this.formData = {
                    ...val,
                    is_default: Boolean(val?.is_default),
                }
                return
            } else {
                this.editMode = false
                this.initializeForm()
            }
        }
    },
    computed: {
        ...mapState({
            authUser: state => state.auth.user
        }),
        ...mapGetters({
            buyer: 'company/buyer',
            isAdmin: 'auth/isAdmin',
            isCompanyAdmin: 'auth/isCompanyAdmin',
            isBuyer: 'auth/isBuyer',
            isRep: 'auth/isRep'
        }),
        isContactInfo () {
            return this.modelName === 'buyerInfo'
        },
        isUserModal () {
            return this.modelName === 'accountUsers'
        },
        isRedesigned () {
            return this.isContactInfo || this.isUserModal
        },
        modalTitle () {
            if (this.isContactInfo) return 'Edit Contact Info'
            return this.modelAction === 'add' ? 'Add User' : 'Edit User'
        },
        authInitials () {
            const { first_name: first = '', last_name: last = '' } = this.authUser || {}
            return `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase()
        },
        newPasswordClass() {
            return !this.formData.new_password ? 'red-background' : '';
        },
        confirmPasswordClass() {
            return !this.formData.confirm_password ? 'red-background' : '';
        }
    },
    methods: {
        ...mapActions({
            updateCompanyBuyer: 'company/updateCompanyBuyer',
            addCompanyBuyer: 'company/addCompanyBuyer',
            fetchAdminContactById: 'contacts/fetchAdminContactById',
            updateActiveDraftOrder: 'orders/updateActiveDraftOrder',
            getCompany: 'company/getCompany',
            getApprovalRequests: 'approvals/getApprovalRequests',
            deleteCompanyBuyer: 'company/deleteCompanyBuyer',
            resetPassword : 'auth/resetPassword',
            updateUserInfo: 'auth/updateUserInfo',
            addUser: 'auth/addUser',
            fetchUser: 'auth/fetchUser'
    }),
     async submitAddBuyerModal () {
        try {
            // Handle user's own profile update (Admin/Rep/Buyer editing their own profile)
            if (this.modelName === 'adminProfile' || this.modelName === 'buyerInfo') {
                if (!this.validateFormData()) {
                    return
                }

                this.formLoading = true

                // Update user information with profile picture if provided
                const formData = new FormData()
                formData.append('_method', 'PATCH')
                formData.append('first_name', this.formData.first_name || '')
                formData.append('last_name', this.formData.last_name || '')
                formData.append('email', this.formData.email || '')
                formData.append('phone_number', this.formData.phone_number || '')
                formData.append('cell_phone_number', this.formData.cell_phone_number || '')

                // Add profile picture if selected
                if (this.profilePictureFile) {
                    formData.append('profile_picture_img_file', this.profilePictureFile)
                }

                const result = await this.updateUserInfo(formData)

                // Handle error response
                if (result.status !== 200) {
                    this.$toast.error(result.message)
                    this.formLoading = false
                    return
                }

                // Update password if provided
                if (this.isPasswordEdit && this.formData.new_password) {
                    const passwordData = {
                        buyer_id: this.authUser.id,
                        is_user: true,
                        password: this.formData.new_password,
                        password_confirmation: this.formData.confirm_password
                    }
                    await this.resetPassword(passwordData)
                }

                // Refresh user data
                await this.fetchUser()

                this.$toast.success('Profile updated successfully')
                this.formLoading = false
                this.shouldEmitOnClose = true
                this.closeModal()
                return
            }

            // Handle company admin adding/editing other users (accountUsers)
            if (this.validateFormData()) {
                this.formLoading = true
                if (this.modelAction === 'add') {
                    if (!this.validatePassword()) {
                        this.formLoading = false
                        return
                    }
                }

                // Convert new_password and confirm_password to password and password_confirmation
                const payload = {
                    ...this.formData,
                    password: this.formData.new_password,
                    password_confirmation: this.formData.confirm_password,
                    company_id: this.companyId
                }

                // Remove new_password and confirm_password from payload
                delete payload.new_password
                delete payload.confirm_password

                // Check if formData.role is COMPANY_ADMIN and update the payload
                if (this.formData.role === ROLES.COMPANY_ADMIN) {
                    payload.role = ROLES.ADMIN
                }

                if (this.editMode) {
                    const response = await this.updateCompanyBuyer({
                        buyerId: this.buyerData.buyer_id ?? this.buyerData.id,
                        payload
                    })
                    await this.getCompany({ companyId: this.companyId})
                    if (this.orderBuyer === this.buyer?.buyer_id) {
                        await this.updateActiveDraftOrder({ orderId: this.orderId, buyer_id: this.orderBuyer })
                    }

                    if (response.message) this.$toast.success(response.message)
                } else {
                    const response = await this.addCompanyBuyer({ payload })
                    if (response.message) this.$toast.success(response.message)
                }

                this.formLoading = false
                this.shouldEmitOnClose = true
                this.closeModal(this.buyer)
                await this.getApprovalRequests()
                await this.fetchAdminContactById(this.companyId)
            }
        } catch (e) {
            if (e.response && e.response.status === 422) {
                // Handle validation errors here
                const validationErrors = e.response.data.errors

                // Convert the validationErrors object into an array of error messages
                const errorMessages = Object.values(validationErrors).flat()

                // Display validation errors to the user
                errorMessages.forEach(errorMessage => {
                    this.$toast.error(errorMessage)
                })
                this.formLoading = false
            }
        }
     },

    resetBuyerInfoError () {
      this.errors.first_name = null
      this.errors.last_name = null
      this.errors.email = null
      this.errors.phone_number = null
      this.errors.cell_phone_number = null
      this.errors.note = null
      this.errors.role = null
      this.errors.new_password = null
      this.errors.confirm_password = null
    },

    validatePassword() {
        let isValidatePassword = true

        if (!this.formData.new_password) {
             this.errors.new_password = 'Please enter new password';
             isValidatePassword = false;
        } else if (this.formData.new_password.length < 8) {
             this.errors.new_password = 'Password must be at least 8 characters';
             isValidatePassword = false;
        } else if (this.formData.new_password.length > 16) {
            this.errors.new_password = 'Password maximum be 16 characters long';
            isValidatePassword = false;
        }

        if (!this.formData.confirm_password) {
             this.errors.confirm_password = 'Please enter confirm password';
             isValidatePassword = false;
        } else if (this.formData.new_password && this.formData.new_password !== this.formData.confirm_password) {
             this.errors.confirm_password = 'Confirm password not matched with new password';
             isValidatePassword = false;
        }

        return isValidatePassword;
     },
    initializeForm () {
      this.formData = {
        is_default: false,
        first_name: '',
        last_name: '',
        phone_number: '',
        cell_phone_number: '',
        email: '',
        note: '',
        buyers_rep: '',
        role: '',
        new_password: '',
        confirm_password: ''
      }
    },
    onProfilePictureChange(event) {
        const file = event.target.files[0]
        if (!file) return

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
        if (!allowedTypes.includes(file.type)) {
            this.$toast.error('Please select a valid image file (JPEG, PNG, GIF)')
            return
        }

        // Validate file size (5MB limit)
        const maxSize = 5 * 1024 * 1024
        if (file.size > maxSize) {
            this.$toast.error('File size must be less than 5MB')
            return
        }

        // Store file for form submission
        this.profilePictureFile = file

        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
            this.profilePicturePreview = e.target.result
        }
        reader.readAsDataURL(file)
    },
    validateFormData() {
        this.resetBuyerInfoError()
        let isValid = true
        const phoneRegex = /^([0-9\s\-\+\(\)]*)$/

        // Trim string values
        Object.keys(this.formData).forEach(key => {
            if (typeof this.formData[key] === 'string') {
                this.formData[key] = this.formData[key].trim()
            }
        })

        // Validate first name
        if (!this.formData.first_name) {
            this.errors.first_name = 'Please enter first name'
            isValid = false
        }

        // Validate last name
        if (!this.formData.last_name) {
            this.errors.last_name = 'Please enter last name'
            isValid = false
        }

        // Validate email
        if (!this.formData.email) {
            this.errors.email = 'Please enter email address'
            isValid = false
        } else if (!this.formData.email.toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)) {
            this.errors.email = 'Please enter valid email address'
            isValid = false
        }

        // Validate phone
        if (!this.formData.phone_number) {
            this.errors.phone_number = 'Please enter phone number'
            isValid = false
        } else if (!phoneRegex.test(this.formData.phone_number)) {
            this.errors.phone_number = 'Invalid phone number'
            isValid = false
        }

        // Validate cell phone
        if (!this.formData.cell_phone_number) {
            this.errors.cell_phone_number = 'Please enter cell phone number'
            isValid = false
        } else if (!phoneRegex.test(this.formData.cell_phone_number)) {
            this.errors.cell_phone_number = 'Invalid cell phone number'
            isValid = false
        }

        // Validate role for accountUsers
        if (this.modelName === 'accountUsers' && this.modelAction === 'add' && !this.formData.role) {
            this.errors.role = 'Please select a role'
            isValid = false
        }

        // Validate password if needed
        if (this.isPasswordEdit && this.formData.new_password) {
            if (!this.validatePassword()) {
                isValid = false
            }
        }

        // Validate password for new users
        if (this.modelAction === 'add') {
            if (!this.validatePassword()) {
                isValid = false
            }
        }

        return isValid
    },
    closeModal (buyer = null) {
      this.initializeForm()
      this.resetBuyerInfoError()
      this.profilePictureFile = null
      this.profilePicturePreview = null
      this.isUploadingPicture = false
        if (this.shouldEmitOnClose || buyer?.buyer_id || buyer?.id) {
            this.$emit('modalClosed', buyer)
        } else {
            this.$emit('modalClosed', null)
        }
      this.$modal.hide('add-buyer-modal')
      this.isPasswordEdit = false
      this.editPasswordText = 'Reset'
    },
    async deleteBuyer () {
        try {
            const message = await this.deleteCompanyBuyer({
                buyerId: this.buyerData.buyer_id ?? this.buyerData.id,
                payload: { company_id: this.companyId }
            });
            if (message) this.$toast.success(message)
        } catch (error) {
            this.$toast.error('An error occurred while deleting the buyer.')
        } finally {
            this.$refs.deleteUserPopperRef.doClose()
            this.$modal.hide('add-buyer-modal')
        }
    },
    async setIsPasswordEdit () {
        if (this.isPasswordEdit && this.validatePassword()) {
            const password = {
                buyer_id: this.buyerData.buyer_id ?? this.buyerData.id,
                password: this.formData.new_password,
                password_confirmation: this.formData.confirm_password
            }
            let response = await this.resetPassword(password)
            this.isPasswordEdit = false
            this.editPasswordText = 'Reset'
            this.$toast.success(response)
            return;
        }

        this.isPasswordEdit = true
        this.editPasswordText = 'Save'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../sass/breakpoints';
@import '../../../../sass/settings-card';

.add-buyer-modal-container {
  ::v-deep {
    .vm--modal {
      display: flex;
      flex-direction: column;
      min-height: 0;
      max-height: 90vh;
      overflow: hidden;
    }
  }
}


.add-buyer-modal-container {
  .card-body {
    padding: 0px;
  }

  .add-buyer-modal-content {
    width: 100%;
    max-width: 1108px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;


    .modal-header {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      background: #f9f9f9;
      border-bottom: 1px solid #dadada;
      padding: 20px 24px;

      svg {
        font-size: 17px;
      }

      h6 {
        margin-bottom: 0px;
        margin-left: 5px;
        margin-right: 17px;
      }

     .modal-required {
        display: flex;
        align-items: center;
     }

     .modal-required-dot {
        margin: auto auto auto 8px;
     }

    }
    .modal-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 24px;

      h5,
      h6 {
        margin-bottom: 0px;
      }

      h5 {
        font: normal normal bold 18px/25px Arial;
        text-transform: uppercase;
      }

      h6 {
        font: normal normal bold 14px/16px Arial;
      }

      .form-label {
        font: normal normal bold 14px/16px Arial;
        letter-spacing: 0px;
        color: #000000;
        margin-right: 10px;
      }

      .form-check-label {
        color: #000000;
      }

      .button-groups {
        margin-top: 30px;
        margin-bottom: 30px;

        .btn-per-item {
          padding: 12px 0;
          width: 145px;
        }

        .delete-icon {
          color: #262626;
          font-size: 20px;
          cursor: pointer;
        }
      }
        .actions-list {
            font: normal normal normal 14px/16px Arial;
            letter-spacing: 0px;
            color: #000000;
            margin-bottom: 11px;
        }
        .container {
            display: block;
            position: relative;
            padding-left: 32px;
            margin-bottom: 11px;
            cursor: pointer;
            font-size: 22px;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
        }
        .container input {
            position: absolute;
            opacity: 0;
            cursor: pointer;
        }
        .checkmark {
            position: absolute;
            top: 0;
            left: 0;
            height: 20px;
            width: 20px;
            background-color: #eee;
            border-radius: 50%;
        }
        .container:hover input ~ .checkmark {
            background-color: #ccc;
        }
        .container input:checked ~ .checkmark {
            background-color: #000000;
        }
        .checkmark:after {
            content: "";
            position: absolute;
            display: none;
        }
        .container input:checked ~ .checkmark:after {
            display: block;
        }
        .container .checkmark:after {
            height: 10px;
            left: 5.5px;
            top: 5px;
            width: 10px;
            border-radius: 50%;
            background: white;
        }

        .actions-list-content {
            font: normal normal normal 14px/16px Arial;
            letter-spacing: 0px;
            color: #000000;
            padding-left: 36px;
            .description {
                font: normal normal normal 14px/15px Arial;
                letter-spacing: 0px;
                color: #C81717;
            }
        }

        .user-permission-status {
            font: normal normal bold 14px/18px Arial;
            margin-left: 16px;
            position:relative
        }

        .user-role {
            color: #CE0202;
        }

        .delete-user {
            font: italic normal normal 14px/16px Arial;
            text-decoration: underline;
            color: #FF0000;
        }

        .permission-popper {
            left: 220px !important;
            top: -12px !important;
            width: 511px !important;
            padding: 0;
        }

        .popover-container {
            padding-right: 0;
            padding-left: 0;
        }

        .bottom-fields-margin {
            margin-top: 16px;
            margin-bottom: 0;
        }

        .password-field-margin {
            margin-top: 50px;
        }

        .save-button {
            height: 38px;
            margin-top: 21px;
            margin-left: 10px;
            display: flex;
            align-items: center;
        }

        .reset-icon {
            margin-left: 10px;
        }

        .buyer-password {
            font: normal normal bold 16px/16px PlayfairDisplay-Bold;
            font-weight: 900;
        }

        .reset-text {
            font-weight: bold;
            text-transform: none;
        }

        .contact-info-popper {
            background: #FBAEAE;
            box-shadow: 0 3px 6px #00000029;
            border: 0.5px solid #898989;
            top : 5px !important;
            left: -9px !important;
        }

        .text-start {
            text-transform: none;
            padding-left: 5px;
            padding-top: 0;
            margin-bottom: 0;
            width: 228px;
        }

        .user-permission-dot {
            display: inline-flex;
            margin-bottom: 0;
        }

        .delete-user-popper-container{
            width: 139px;
        }

        .delete-user-popper-container-text {
            padding-left: 15px;
            text-align: left;
            font: normal normal normal 14px / 25px Arial;
            color: black;
        }

        .delete-user-yes {
            padding-right: 20px;
            text-decoration: underline;
            font: italic normal normal 14px / 15px Arial;
            color: #31B4D6;
            cursor: pointer
        }

        .delete-user-no {
            text-decoration: underline;
            font: italic normal normal 14px / 15px Arial;
            color: #31B4D6;
            cursor: pointer
        }

        .profile-section {
            .profile-picture-wrapper {
                display: flex;
                align-items: center;

                .profile-image-container {
                    .profile-image {
                        object-fit: cover;
                        border: 2px solid #e0e0e0;
                    }

                    .profile-placeholder {
                        width: 120px;
                        height: 120px;
                        background: #f5f5f5;
                        border: 2px solid #e0e0e0;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        .profile-icon {
                            font-size: 50px;
                            color: #999;
                        }
                    }
                }

                .profile-edit-icon {
                    cursor: pointer;
                    color: #000;
                    font-size: 18px;

                    &.disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }

                    &:hover:not(.disabled) {
                        color: #333;
                    }
                }
            }
        }

        .section-title {
            font: normal normal bold 16px/20px Arial;
            color: #000;
            margin-bottom: 0;
        }

    }

    @media screen and (max-width: 768px) {
      .modal-header {
        padding: 12px 14px !important;
        flex-wrap: wrap;
        gap: 6px;

        h6 {
          margin-right: 10px;
        }
      }

      .modal-body {
        padding: 20px;

        .row {
          row-gap: 14px;
        }

        .row.mb-3 {
          margin-bottom: 14px !important;
        }

        .row.mb-4 {
          margin-bottom: 20px !important;
        }

        .permission-popper {
          left: 0 !important;
          top: 30px !important;
          width: calc(100vw - 60px) !important;
          max-width: 511px;
        }

        .profile-section {
          .profile-picture-wrapper {
            flex-wrap: wrap;
            gap: 12px;
          }
        }

        .password-field-margin {
          margin-top: 16px;
        }

        .save-button {
          margin-top: 12px;
          margin-left: 0;
        }

        .button-groups {
          margin-top: 24px !important;
          margin-bottom: 16px !important;
          flex-direction: column;
          align-items: stretch !important;
          gap: 12px;

          .d-flex {
            width: 100%;
            flex-wrap: wrap;
            gap: 10px;

            .btn-per-item {
              flex: 1 1 0;
              width: auto;
            }
          }
        }
      }
    }

    @media screen and (max-width: 575px) {
      .modal-header {
        padding: 12px 14px;

        h6 {
          font-size: 14px;
          margin-right: 8px;
        }
      }

      .modal-body {
        padding: 16px;

        .row {
          row-gap: 12px;
        }

        .row.mb-3 {
          margin-bottom: 12px !important;
        }

        .profile-section {
          .profile-picture-wrapper {
            .profile-image-container {
              .profile-image,
              .profile-placeholder {
                width: 90px;
                height: 90px;
              }

              .profile-placeholder .profile-icon {
                font-size: 36px;
              }
            }
          }
        }

        .section-title {
          font-size: 14px;
          line-height: 18px;
        }

        .button-groups {
          .btn-per-item {
            padding: 10px 0;
            font-size: 14px;
          }
        }

        .user-permission-status {
          margin-left: 0;
          font-size: 12px;
          line-height: 16px;
        }
      }
    }
  }
}

// Edit Contact Info (buyerInfo) design. Nested one level deeper than the shared rules above so it
// wins without touching the other modes (accountUsers, adminProfile) that reuse this modal.
$contact-required: #b23b3b;

.add-buyer-modal-container .add-buyer-modal-content.contact-modal {
  color: $sc-text;
  font-family: Arial, Helvetica, sans-serif;

  .contact-modal__header {
    display: flex;
    flex: 0 0 auto;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 32px 38px 24px;
    border-bottom: 1px solid $sc-border;

    @include tablet-mini-down {
      padding: 20px;
    }
  }

  .contact-modal__eyebrow {
    margin: 0 0 12px;
    color: $sc-gold;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
  }

  .contact-modal__title {
    margin: 0;
    font-size: 26px;
    font-weight: 400;
    line-height: 1.2;
  }

  .contact-modal__header-side {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-top: 2px;
  }

  .contact-modal__required {
    margin: 0;
    color: $sc-muted;
    font-size: 12px;

    span {
      color: $contact-required;
    }
  }

  .contact-modal__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-right: -12px;
    padding: 0;
    background: transparent;
    border: 0;
    color: $sc-text;
    cursor: pointer;
  }

  .modal-body {
    padding: 28px 38px 0;

    @include tablet-mini-down {
      padding: 20px 20px 0;
    }

    .row {
      --bs-gutter-x: 24px;
    }

    .mb-5.mt-3 {
      margin: 0 0 24px !important;
    }

    ::v-deep .form-label {
      margin: 0 0 10px;
      color: $sc-text;
      font: 700 14px/16px Arial, Helvetica, sans-serif;
    }

    ::v-deep .required-dot {
      width: auto;
      height: auto;
      margin: 0 0 10px 6px;
      background: none;
      border-radius: 0;

      &::before {
        content: '*';
        color: $contact-required;
        font-size: 14px;
        line-height: 16px;
      }
    }

    ::v-deep .form-control {
      min-height: 46px;
      padding: 10px 14px;
      border: 1px solid $sc-border;
      border-radius: 0;
      font-size: 14px;
    }
  }

  .contact-modal__notice {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 24px;
    padding: 18px 22px;
    background: $sc-sand;
    font-size: 14px;
    line-height: 20px;

    svg {
      flex: 0 0 auto;
      color: $sc-gold;
    }
  }

  .contact-modal__permission {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 20px;
  }

  .contact-modal__avatar {
    display: flex;
    flex: 0 0 52px;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border: 1px solid $sc-sand-border;
    border-radius: 50%;
    background: $sc-sand;
    font-size: 16px;
    object-fit: cover;
  }

  .contact-modal__permission-label {
    @include sc-label;
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 6px;
    font-size: 10px;
  }

  .contact-modal__info {
    display: inline-flex;
    cursor: help;

    &:focus-visible {
      outline: 2px solid $sc-text;
      outline-offset: 2px;
    }
  }

  // Same badge as the Contact Info card (ContactInfoSection).
  .contact-modal__role {
    display: inline-block;
    padding: 4px 10px;
    background: $sc-navy;
    color: #ffffff;
    font: 700 9px/1.2 Arial, Helvetica, sans-serif;
    letter-spacing: 0.2em;
    text-transform: uppercase;
  }

  .contact-modal__permission-text {
    flex: 1 1 260px;
    margin: 0;
    color: $sc-muted;
    font-size: 12px;
  }

  // Only the fields scroll; the notice stays above and the action bar stays pinned below.
  .modal-body {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > form {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
    }
  }

  .contact-modal__scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding-bottom: 24px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .modal-body .button-groups {
    flex: 0 0 auto;
    margin: 0 -38px;
    padding: 20px 38px;
    background: $sc-sand;
    border-top: 1px solid $sc-border;

    @include tablet-mini-down {
      margin: 0 -20px;
      padding: 16px 20px;
    }

    > .d-flex {
      flex-direction: row-reverse;
      gap: 14px;
      width: 100%;
      justify-content: flex-start;
    }

    .btn-per-item {
      width: auto;
      min-height: 46px;
      margin: 0 !important;
      padding: 0 26px;
      font: 700 12px/1 Arial, Helvetica, sans-serif;
      letter-spacing: $sc-tracking;
      text-transform: uppercase !important;

      @include tablet-mini-down {
        flex: 1 1 0;
      }

      &:focus-visible {
        outline: 2px solid $sc-text;
        outline-offset: 2px;
      }
    }

    .p-btn-primary:not(.bg-secondary) {
      background: $sc-navy;
      border-color: $sc-navy;
    }

    .bg-secondary {
      background: #ffffff !important;
      border: 1px solid $sc-border;
      color: $sc-text;
    }
  }
}

// Edit/Add User (accountUsers). Layers on the shared .contact-modal rules above.
.add-buyer-modal-container .add-buyer-modal-content.user-modal {
  .user-modal__permission-col {
    padding-right: 0;
    padding-left: 0;
  }

  .user-modal__permission {
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .user-modal__legend {
    float: none;
    width: auto;
    margin: 0 0 14px;
    padding: 0;
    font: 700 14px/16px Arial, Helvetica, sans-serif;
  }

  .user-modal__required-mark {
    margin-left: 6px;
    color: $contact-required;
  }

  .user-modal__roles {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;

    @include tablet-mini-down {
      grid-template-columns: 1fr;
    }
  }

  .user-modal__role {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    min-height: 118px;
    margin: 0;
    padding: 20px 18px;
    border: 1px solid $sc-border;
    background: #ffffff;
    cursor: pointer;

    &.is-selected {
      border-color: $sc-text;
      background: $sc-sand;
    }
  }

  .user-modal__role-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .user-modal__radio {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    border: 1px solid $sc-muted;
    border-radius: 50%;
    background: #ffffff;
  }

  .is-selected .user-modal__radio {
    border: 5px solid $sc-navy;
  }

  .user-modal__role-input:focus-visible ~ .user-modal__radio {
    outline: 2px solid $sc-text;
    outline-offset: 2px;
  }

  .user-modal__role-name {
    display: block;
    margin-bottom: 8px;
    font: 700 12px/16px Arial, Helvetica, sans-serif;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  .user-modal__role-desc {
    display: block;
    color: $sc-muted;
    font-size: 12px;
    line-height: 16px;
  }

  .user-modal__password {
    margin-top: 8px;
    padding-top: 24px;
    border-top: 1px solid $sc-border;
  }

  .user-modal__password-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    @include tablet-mini-down {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .user-modal__password-title {
    margin: 0 0 4px;
    font: 700 14px/16px Arial, Helvetica, sans-serif;
  }

  .user-modal__password-text {
    margin: 0;
    color: $sc-muted;
    font-size: 14px;
  }

  .user-modal__password-fields {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    align-items: end;
    gap: 16px;

    @include tablet-mini-down {
      grid-template-columns: 1fr;
    }
  }

  .user-modal__outline-btn {
    min-height: 46px;
    padding: 0 26px;
    background: #ffffff;
    border: 1px solid $sc-text;
    color: $sc-text;
    font: 700 12px/1 Arial, Helvetica, sans-serif;
    letter-spacing: $sc-tracking;
    text-transform: uppercase;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $sc-text;
      outline-offset: 2px;
    }
  }

  .modal-body .button-groups.user-modal__footer {
    justify-content: space-between;
    align-items: center;

    > .d-flex {
      width: auto;
      margin-left: auto;
    }

    .delete-icon {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 0;
      background: none;
      border: 0;
      color: $contact-required;
      font-size: 14px;
      cursor: pointer;

      .delete-user {
        text-decoration: underline;
      }

      &:focus-visible {
        outline: 2px solid $sc-text;
        outline-offset: 2px;
      }
    }

    @include tablet-mini-down {
      flex-wrap: wrap;
      gap: 16px;

      > .d-flex {
        width: 100%;
      }
    }
  }
}
</style>

<style>
@media (max-width: 1150px) {
  .vm--modal.add-buyer-vm-modal {
    width: calc(100vw - 48px) !important;
    max-width: calc(100vw - 48px) !important;
    max-height: 88vh !important;
    border-radius: 8px !important;
  }
}

@media (max-width: 768px) {
  .vm--modal.add-buyer-vm-modal {
    width: calc(100vw - 40px) !important;
    max-width: calc(100vw - 40px) !important;
    max-height: 88vh !important;
  }
}

@media (max-width: 480px) {
  .vm--modal.add-buyer-vm-modal {
    width: calc(100vw - 32px) !important;
    max-width: calc(100vw - 32px) !important;
    max-height: 90vh !important;
    border-radius: 10px !important;
  }
}
</style>
